import { Box, Text, useMediaQuery, useTheme } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import UnderlineIcon from '@/assets/icons/ic-nav-underline.svg';

import { eventConfigurationQueryOptions } from '@/queries/upgrade/eventConfiguration';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { ThinEvent } from '@/resources/ThinEvent';
import { AnyProfile } from '@/utils/profileHelpers';

import { EventNavigationItem } from '../EventNavigationItem/EventNavigationItem';
import { MobileUserNavigation } from '../MobileUserNavigation/MobileUserNavigation';
import { useNavigationRailStore } from '../NavigationRail/NavigationRail.store';
import { PrimaryNavigationItems } from '../PrimaryNavigationItems/PrimaryNavigationItems';

// What actually lives inside the nav rail/drawer: which pages are enabled for this event, the
// signed-in user's account actions, and the link back to the Underline homepage. Rendered
// identically regardless of whether NavigationRail is currently showing it as a rail or a
// drawer - `enableTooltip` is the only thing here that cares about viewport size.
export const NavigationRailContent: FC<{
  event: ThinEvent;
  currentUserProfile: AnyProfile;
  onLogout: () => void;
}> = ({ event, currentUserProfile, onLogout }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const [isMobileViewport] = useMediaQuery(`(max-width: ${breakpoints.lg})`);
  const { data: session } = useQuery(sessionQueryOptions());
  const { data: eventConfiguration } = useQuery(eventConfigurationQueryOptions(event?.id));
  const isExpanded = useNavigationRailStore((state) => state.isExpanded);
  // the label itself is on screen once the rail is expanded, so the tooltip would just repeat it
  const enableTooltip = !isMobileViewport && !isExpanded;

  return (
    <Fragment>
      <PrimaryNavigationItems
        eventConfiguration={eventConfiguration}
        eventId={router.query.eventId}
        currentRoute={router.asPath}
        enableTooltip={enableTooltip}
      />

      {/* Pushes everything below (profile/log-in + the Underline logo) to the
          bottom in every state. Must live here rather than on the log-in link,
          which is display:none on desktop and so can't anchor the logo. */}
      <Box mt="auto" />

      <MobileUserNavigation
        user={session?.user}
        currentUserProfile={currentUserProfile}
        enableTooltip={enableTooltip}
        redirectUrl={router.asPath}
        onLogout={onLogout}
      />

      <NextLink href="/" passHref legacyBehavior>
        <EventNavigationItem
          enableTooltip={enableTooltip}
          Icon={<UnderlineIcon height="1.5rem" width="1.5rem" />}
          aria-label="Go to homepage"
          minH="3.5rem"
        >
          <Text as="span" fontSize="md" lineHeight="base" textTransform="capitalize">
            {t('underline')}
          </Text>
        </EventNavigationItem>
      </NextLink>
    </Fragment>
  );
};
